import {Button, Card, Collapse, Flex, Layout, Typography} from "antd";
import {ArrowRightOutlined} from "@ant-design/icons";
import './UserDetails.css'
import {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {HttpService} from "../../services/http/Http.service";
import {UtilsService} from "../../services/Utils.service";
import PracticeDetailPanel from "./PracticeDetailPanel";
import {Person, ProcessedPracticeDetail} from "../../model/models";
import ProfileCard from "./ProfileCard";
import {HttpErrorService} from "../../services/http/HttpError.service";

const {Sider, Content} = Layout;
const {Title} = Typography;

export default function UserDetails() {
    const { id, combinatorIdFromExt } = useParams();
    const personId = Number(id)
    const [person, setPerson] = useState<Person>()
    const navigate = useNavigate();
    const profileCardRef = useRef<{ fetchDocuments: () => void }>(null);
    const [hasPaidInstallments, setHasPaidInstallments] = useState(false);

    const fetchPerson = async () => {
        try {
            const data = await HttpService.getInstanceAxios().get(`/person/${personId}`);
            setPerson(data.data);
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero dei dati dell'utente", error, true);
        }
    };

    useEffect(() => {
        if (personId) {
            fetchPerson();
            setDocumentSectionOpened(false);
        }
    }, [personId]);

    const [selectedPractice, setSelectedPractice] = useState<ProcessedPracticeDetail | undefined>(undefined);
    const [documentSectionOpened, setDocumentSectionOpened] = useState<boolean>(false);

    const showModal = (practice: ProcessedPracticeDetail) => {
        setSelectedPractice(practice);
        setDocumentSectionOpened(true)
        setCombinatorId(practice.simulatorId)
        setHasPaidInstallments(practice.numberOfPaidInstallments > 0);
        navigate(`/Fascicolo/DettagliUtente/${personId}/${practice.simulatorId}`);
    };

    const [listOfPractices, setListOfPractices] = useState<ProcessedPracticeDetail[]>([])
    const [combinatorId, setCombinatorId] = useState<number>()

    const loadPractices = async (shouldUpdateSelected = false) => {
        try {
            const { data } = await HttpService.getInstanceAxios().get(`/file-ts/person/${personId}/details`);
            setListOfPractices(data);

            if (shouldUpdateSelected && selectedPractice) {
                const updated = data.find((p: ProcessedPracticeDetail) => p.simulatorId === selectedPractice.simulatorId);
                if (updated) {
                    setSelectedPractice(updated);
                }
            }
        } catch (error) {
            HttpErrorService.handle("Errore durante il recupero delle pratiche dell'utente", error, true);
        }
    };


    useEffect(() => {
        loadPractices();
    }, [personId]);

    useEffect(() => {
        if (combinatorIdFromExt && listOfPractices.length > 0) {
            const matched = listOfPractices.find(p => p.simulatorId === Number(combinatorIdFromExt));
            if (matched) {
                setSelectedPractice(matched);
                setCombinatorId(matched.simulatorId);
                setDocumentSectionOpened(true);
            }
        }
    }, [combinatorIdFromExt, listOfPractices, personId]);

    const groupedByYear: { [year: string]: ProcessedPracticeDetail[] } = {};

    listOfPractices.forEach((item) => {
        const year = item.firstDocumentYear
            ? new Date(item.firstDocumentYear).getFullYear().toString()
            : "In attesa di firma";
        if (!groupedByYear[year]) {
            groupedByYear[year] = [];
        }
        groupedByYear[year].push(item);
    });

    return (
        <Layout>
            <Sider width={400} style={{background: "white", padding: 12}}>
                <ProfileCard person={person} documentSectionOpened={documentSectionOpened} setPerson={setPerson} simulatorId={combinatorId} ref={profileCardRef}  hasPaidInstallments={hasPaidInstallments} refreshPractices={() => loadPractices(true)}/>
            </Sider>
            <Layout>
                {listOfPractices.length > 0 ?
                    <Content style={{padding: 24}}>
                        {selectedPractice ? (
                            person &&
                            <PracticeDetailPanel
                                setPerson={setPerson}
                                person={person}
                                simulatorId={combinatorId}
                                practice={selectedPractice}
                                onBack={() => {
                                    setSelectedPractice(undefined)
                                    setDocumentSectionOpened(false)
                                    navigate(`/Fascicolo/DettagliUtente/${personId}`);
                                    loadPractices();

                                }}
                                refreshPractices={() => loadPractices(true)}
                                refreshPerson={fetchPerson}
                                refreshDocuments={() => profileCardRef.current?.fetchDocuments()}
                                onPaidInstallmentsChange={setHasPaidInstallments}
                            />
                        ) : (
                            <Collapse
                                accordion
                                size="large"
                                expandIconPosition="end"
                                items={Object.entries(groupedByYear).map(([year, practices]) => ({
                                    key: year,
                                    label: <strong>{year}</strong>,
                                    children: (
                                        <div style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "20px",
                                            alignItems: "flex-start"
                                        }}>
                                            {practices.map((item: ProcessedPracticeDetail) => (
                                                <Card
                                                    key={item.protocolNumber}
                                                    title={
                                                        <Flex align="center" justify="space-between">
                                                            <Title level={3}>{item.type}</Title>
                                                            <Button onClick={() => showModal(item)} type="primary" icon={<ArrowRightOutlined />} />
                                                        </Flex>
                                                    }
                                                    style={{
                                                        background: "#FAFAFA",
                                                        width: "fit-content",
                                                        minWidth: "350px",
                                                        maxWidth: "500px",
                                                        flex: "0 1 auto"
                                                    }}
                                                >
                                                    <div className="data-container"><strong>Importo totale:</strong> € {UtilsService.formatNumber(item.total)}</div>
                                                    <div className="data-container"><strong>Codice esenzione:</strong> {item.exemptionCodes}</div>
                                                    <div className="data-container"><strong>Stato:</strong> {item.status}</div>
                                                    <div className="data-container"><strong>Data notifica:</strong> {UtilsService.formatDateString(item.communicationDate)}</div>
                                                    <div className="data-container"><strong>Rate:</strong> {item.numberOfInstallments > 1 ? "Si" : "No"}</div>
                                                    <div className="data-container"><strong>Pagato:</strong> {item.paidUniqueInstallment ? 'Pagata Rata Unica' : (`${item.numberOfPaidInstallments}/${item.numberOfInstallments}`)}</div>
                                                    <div className="data-container"><strong>Protocollo archiviazione:</strong> {item.protocolNumber}</div>
                                                    <div className="data-container"><strong>Data archiviazione:</strong> {UtilsService.formatDateString(item.createdAt)}</div>
                                                </Card>
                                            ))}
                                        </div>
                                    )
                                }))}
                            ></Collapse>

                        )}
                    </Content>
                    : <Title level={3}>Nessuna pratica protocollata</Title>}
            </Layout>
        </Layout>
    );
};